import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useSourcesStore } from '@/stores';
import { EngineService } from '@/services/engine';
import {
    useDebounce,
    useFeatureFlags,
    useSourceFoldersInfinite,
} from '@/hooks';
import { toast } from '@/components';
import {
    FullSource,
    SourceType,
    SourceVisibility,
    getSelectableSourceType,
} from '@/types/source';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { SEARCH_DEBOUNCE_MS } from '@/constants/debounce';
import { SourceFolder } from '@/types/source-folder';

const PAGE_SIZE = 12;

export const SourceFolderId = {
    ALL_VIDEOS: 'all-videos',
    ALL_CAMERAS: 'all-cameras',
    MY_VIDEOS: 'my-videos',
    MY_CAMERAS: 'my-cameras',
    SHARED_VIDEOS: 'shared-videos',
    SHARED_CAMERAS: 'shared-cameras',
} as const;

export type SourceFolderId =
    | (typeof SourceFolderId)[keyof typeof SourceFolderId]
    | string;

export const BUILT_IN_TYPE_FOLDER_IDS = new Set<string>([
    SourceFolderId.ALL_VIDEOS,
    SourceFolderId.ALL_CAMERAS,
    SourceFolderId.MY_VIDEOS,
    SourceFolderId.MY_CAMERAS,
    SourceFolderId.SHARED_VIDEOS,
    SourceFolderId.SHARED_CAMERAS,
]);

const VIDEO_TO_CAMERA_FOLDER_ID: Record<string, string> = {
    [SourceFolderId.ALL_VIDEOS]: SourceFolderId.ALL_CAMERAS,
    [SourceFolderId.MY_VIDEOS]: SourceFolderId.MY_CAMERAS,
    [SourceFolderId.SHARED_VIDEOS]: SourceFolderId.SHARED_CAMERAS,
};

const CAMERA_TO_VIDEO_FOLDER_ID: Record<string, string> = {
    [SourceFolderId.ALL_CAMERAS]: SourceFolderId.ALL_VIDEOS,
    [SourceFolderId.MY_CAMERAS]: SourceFolderId.MY_VIDEOS,
    [SourceFolderId.SHARED_CAMERAS]: SourceFolderId.SHARED_VIDEOS,
};

function resolveEnabledBuiltInFolderId(
    activeFolderId: string,
    camerasEnabled: boolean,
    videosEnabled: boolean,
): string | null {
    if (!videosEnabled) {
        return VIDEO_TO_CAMERA_FOLDER_ID[activeFolderId] ?? null;
    }
    if (!camerasEnabled) {
        return CAMERA_TO_VIDEO_FOLDER_ID[activeFolderId] ?? null;
    }
    return null;
}

interface BuiltInScopeQuery {
    type: SourceType;
    visibility: SourceVisibility;
}

const BUILT_IN_SCOPE_QUERY: Record<string, BuiltInScopeQuery> = {
    [SourceFolderId.ALL_CAMERAS]: {
        type: SourceType.CAMERA,
        visibility: 'all',
    },
    [SourceFolderId.MY_CAMERAS]: {
        type: SourceType.CAMERA,
        visibility: 'private',
    },
    [SourceFolderId.SHARED_CAMERAS]: {
        type: SourceType.CAMERA,
        visibility: 'organization',
    },
    [SourceFolderId.ALL_VIDEOS]: { type: SourceType.FILE, visibility: 'all' },
    [SourceFolderId.MY_VIDEOS]: {
        type: SourceType.FILE,
        visibility: 'private',
    },
    [SourceFolderId.SHARED_VIDEOS]: {
        type: SourceType.FILE,
        visibility: 'organization',
    },
};

interface UseSourcesDataOptions {
    includeVideos?: boolean;
    includeCameras?: boolean;
    excludeEmptyFolders?: boolean;
}

interface SourcesFolderUIItem {
    id: string;
    name: string;
    count: number;
    isLoading: boolean;
    isDynamic: boolean;
    isSharedByOwner?: boolean;
}

export interface SourceFolderGroup {
    id: string;
    title: string;
    count: number;
    defaultExpanded: boolean;
    items: SourcesFolderUIItem[];
}

function toFolderItem(folder: SourceFolder): SourcesFolderUIItem {
    return {
        id: folder.id,
        name: folder.name,
        count: folder.sourceCount ?? 0,
        isLoading: false,
        isDynamic: true,
        isSharedByOwner: folder.isOwner && folder.isShared,
    };
}

function orderByCount(
    camerasItem: SourcesFolderUIItem | null,
    videosItem: SourcesFolderUIItem | null,
): SourcesFolderUIItem[] {
    if (!camerasItem) return videosItem ? [videosItem] : [];
    if (!videosItem) return [camerasItem];
    return videosItem.count > camerasItem.count
        ? [videosItem, camerasItem]
        : [camerasItem, videosItem];
}

function mergeSourcesById(
    previous: readonly FullSource[],
    incoming: readonly FullSource[],
): FullSource[] {
    const byId = new Map(previous.map((source) => [source.id, source]));
    incoming.forEach((source) => byId.set(source.id, source));
    return Array.from(byId.values());
}

function filterSourcesByType(
    sources: readonly FullSource[],
    type: SourceType | undefined,
): FullSource[] {
    if (!type) return [...sources];
    return sources.filter(
        (source) => getSelectableSourceType(source.type) === type,
    );
}

function reconcileWithStore(
    sources: readonly FullSource[],
    storeSourcesMap: ReadonlyMap<string, FullSource>,
): FullSource[] {
    return sources
        .map((source) => storeSourcesMap.get(source.id))
        .filter((source): source is FullSource => source !== undefined);
}

export function useSourcesData({
    includeVideos = true,
    includeCameras = true,
    excludeEmptyFolders = false,
}: UseSourcesDataOptions = {}) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const { liveCamerasEnabled, hdmiEnabled, videoFilesEnabled } =
        useFeatureFlags();
    const camerasEnabled =
        includeCameras && (liveCamerasEnabled || hdmiEnabled);
    const videosEnabled = includeVideos && videoFilesEnabled;
    const activeSourceType: SourceType | undefined =
        camerasEnabled !== videosEnabled
            ? camerasEnabled
                ? SourceType.CAMERA
                : SourceType.FILE
            : undefined;
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);
    const [activeFolderId, setActiveFolderId] = useState<SourceFolderId>(
        SourceFolderId.MY_CAMERAS,
    );
    const [hasUserSelectedFolder, setHasUserSelectedFolder] = useState(false);
    const [hasResolvedDefaultFolder, setHasResolvedDefaultFolder] =
        useState(false);

    const selectFolder = useCallback((folderId: SourceFolderId) => {
        setHasUserSelectedFolder(true);
        setActiveFolderId(folderId);
    }, []);
    const [camerasPage, setCamerasPage] = useState(1);
    const [videosPage, setVideosPage] = useState(1);
    const [scopeSearchPage, setScopeSearchPage] = useState(1);
    const [scopeSearchResults, setScopeSearchResults] = useState<FullSource[]>(
        [],
    );
    const [folderSourcesByFolderId, setFolderSourcesByFolderId] = useState<
        Readonly<Record<string, readonly FullSource[]>>
    >({});

    const addSources = useSourcesStore((state) => state.addSources);
    const storeSourcesMap = useSourcesStore((state) => state.sourcesMap);
    const [allCameras, setAllCameras] = useState<FullSource[]>([]);
    const [allVideos, setAllVideos] = useState<FullSource[]>([]);
    const myCameras = useMemo(
        () => allCameras.filter((s) => s.isOwner),
        [allCameras],
    );
    const myVideos = useMemo(
        () => allVideos.filter((s) => s.isOwner),
        [allVideos],
    );
    const sharedCameras = useMemo(
        () => allCameras.filter((s) => !s.isOwner),
        [allCameras],
    );
    const sharedVideos = useMemo(
        () => allVideos.filter((s) => !s.isOwner),
        [allVideos],
    );

    const {
        data: camerasData,
        isLoading: camerasLoading,
        isValidating: camerasValidating,
        error: camerasError,
    } = useSWR(
        [CACHE_KEYS.SOURCES_PREFIX, SourceType.CAMERA, camerasPage, PAGE_SIZE],
        ([_key, type, page, size]) =>
            EngineService.getSources({
                type,
                page,
                size,
                orderBy: 'createdAt desc, id desc',
            }),
        { keepPreviousData: true },
    );

    const {
        data: videosData,
        isLoading: videosLoading,
        isValidating: videosValidating,
        error: videosError,
    } = useSWR(
        includeVideos
            ? [
                  CACHE_KEYS.SOURCES_PREFIX,
                  SourceType.FILE,
                  videosPage,
                  PAGE_SIZE,
              ]
            : null,
        ([_key, type, page, size]) =>
            EngineService.getSources({
                type,
                page,
                size,
                orderBy: 'createdAt desc, id desc',
            }),
        { keepPreviousData: true },
    );

    const { data: myCamerasCountData } = useSWR(
        [CACHE_KEYS.SOURCES_PREFIX, SourceType.CAMERA, 'private', 'count'],
        ([_key, type, visibility]) =>
            EngineService.getSources({
                type,
                visibility: visibility as SourceVisibility,
                page: 1,
                size: 1,
            }),
    );

    const { data: myVideosCountData } = useSWR(
        includeVideos
            ? [CACHE_KEYS.SOURCES_PREFIX, SourceType.FILE, 'private', 'count']
            : null,
        ([_key, type, visibility]) =>
            EngineService.getSources({
                type,
                visibility: visibility as SourceVisibility,
                page: 1,
                size: 1,
            }),
    );

    const {
        data: sourceFoldersPages,
        isLoading: sourceFoldersLoading,
        error: sourceFoldersError,
        size: sourceFoldersSize,
        setSize: setSourceFoldersSize,
        isValidating: sourceFoldersValidating,
        hasMore: hasMoreFolders,
        loadMore: loadMoreFolders,
    } = useSourceFoldersInfinite(excludeEmptyFolders, {
        revalidateFirstPage: true,
        revalidateOnMount: true,
    });

    const loadedFolders = useMemo(() => {
        if (!sourceFoldersPages) return;
        return sourceFoldersPages.flatMap((page) => page.items);
    }, [sourceFoldersPages]);

    const unloadedFolderIds = useMemo(
        () =>
            (loadedFolders ?? [])
                .map((folder) => folder.id)
                .filter(
                    (folderId) =>
                        !folderSourcesByFolderId[folderId] &&
                        folderId !== activeFolderId,
                ),
        [loadedFolders, folderSourcesByFolderId, activeFolderId],
    );

    useSWR(
        unloadedFolderIds.length > 0
            ? [
                  CACHE_KEYS.SOURCES_PREFIX,
                  'folder-children',
                  ...unloadedFolderIds,
              ]
            : null,
        async () =>
            Promise.all(
                unloadedFolderIds.map(async (folderId) => {
                    const children =
                        await EngineService.getSourceFolderChildren(folderId);
                    return [folderId, children.sources] as const;
                }),
            ),
        {
            revalidateOnFocus: false,
            onSuccess: (entries) => {
                addSources(entries.flatMap(([, sources]) => [...sources]));
                setFolderSourcesByFolderId((previous) => ({
                    ...previous,
                    ...Object.fromEntries(entries),
                }));
            },
        },
    );

    const isBuiltInSidebarItem = BUILT_IN_TYPE_FOLDER_IDS.has(activeFolderId);

    const {
        data: activeFolderChildren,
        isLoading: activeFolderChildrenLoading,
        error: activeFolderChildrenError,
    } = useSWR(
        !isBuiltInSidebarItem
            ? [CACHE_KEYS.SOURCES_PREFIX, 'folder', activeFolderId]
            : null,
        ([_key, _folderType, folderId]) =>
            EngineService.getSourceFolderChildren(folderId),
        { revalidateOnMount: true },
    );

    const activeScopeQuery = BUILT_IN_SCOPE_QUERY[activeFolderId];
    const useBackendScope = Boolean(
        activeScopeQuery &&
        (activeScopeQuery.visibility !== 'all' || debouncedSearchQuery),
    );

    const { data: activeScopeSearchSourceIds } = useSWR(
        activeScopeQuery && debouncedSearchQuery
            ? [
                  CACHE_KEYS.SOURCES_PREFIX,
                  'ids',
                  activeScopeQuery.type,
                  activeScopeQuery.visibility,
                  debouncedSearchQuery,
              ]
            : null,
        ([_key, _ids, type, visibility, search]) =>
            EngineService.getSourceIds({ type, visibility, search }),
    );

    const scopeIdQueries = useMemo(
        () =>
            Object.entries(BUILT_IN_SCOPE_QUERY).filter(
                ([, query]) =>
                    query.visibility !== 'all' &&
                    (query.type === SourceType.CAMERA
                        ? camerasEnabled
                        : videosEnabled),
            ),
        [camerasEnabled, videosEnabled],
    );

    const { data: scopeSourceIdEntries } = useSWR(
        scopeIdQueries.length > 0
            ? [
                  CACHE_KEYS.SOURCES_PREFIX,
                  'scope-ids',
                  ...scopeIdQueries.map(([scopeId]) => scopeId),
              ]
            : null,
        async () =>
            Promise.all(
                scopeIdQueries.map(async ([scopeId, query]) => {
                    const ids = await EngineService.getSourceIds({
                        type: query.type,
                        visibility: query.visibility,
                    });
                    return [scopeId, ids] as const;
                }),
            ),
    );

    const scopeSourceIdsByScopeId = useMemo<Record<string, readonly string[]>>(
        () => Object.fromEntries(scopeSourceIdEntries ?? []),
        [scopeSourceIdEntries],
    );

    const {
        data: scopeSearchData,
        isLoading: scopeSearchLoading,
        error: scopeSearchError,
    } = useSWR(
        useBackendScope
            ? [
                  CACHE_KEYS.SOURCES_PREFIX,
                  'scope-search',
                  activeScopeQuery.type,
                  activeScopeQuery.visibility,
                  debouncedSearchQuery,
                  scopeSearchPage,
                  PAGE_SIZE,
              ]
            : null,
        ([_key, _tag, type, visibility, search, page, size]) =>
            EngineService.getSources({
                type,
                visibility,
                search: search || undefined,
                page,
                size,
                orderBy: 'createdAt desc, id desc',
            }),
    );

    useEffect(() => {
        setScopeSearchResults([]);
        setScopeSearchPage(1);
    }, [activeFolderId, debouncedSearchQuery]);

    useEffect(() => {
        if (!scopeSearchData?.items) return;
        setScopeSearchResults((prev) => {
            const byId = new Map(prev.map((source) => [source.id, source]));
            scopeSearchData.items.forEach((source) =>
                byId.set(source.id, source),
            );
            return Array.from(byId.values());
        });
    }, [scopeSearchData]);

    const scopeSearchHasNextPage = scopeSearchData
        ? scopeSearchData.page.page < scopeSearchData.page.totalPages
        : false;

    useEffect(() => {
        const errorType = camerasError
            ? 'cameras'
            : includeVideos && videosError
              ? 'videos'
              : scopeSearchError
                ? 'search results'
                : sourceFoldersError
                  ? 'source folders'
                  : activeFolderChildrenError
                    ? 'folder sources'
                    : null;
        if (errorType) {
            toast.error(`Failed to load ${errorType}. Please try again.`);
        }
    }, [
        camerasError,
        videosError,
        scopeSearchError,
        sourceFoldersError,
        activeFolderChildrenError,
        includeVideos,
    ]);

    useEffect(() => {
        if (camerasData?.items) {
            addSources(camerasData.items);
            setAllCameras((prev) => mergeSourcesById(prev, camerasData.items));
        }
    }, [camerasData, addSources]);

    useEffect(() => {
        if (includeVideos && videosData?.items) {
            addSources(videosData.items);
            setAllVideos((prev) => mergeSourcesById(prev, videosData.items));
        }
    }, [videosData, addSources, includeVideos]);

    useEffect(() => {
        if (storeSourcesMap.size === 0) return;
        setAllCameras((prev) => reconcileWithStore(prev, storeSourcesMap));
        setAllVideos((prev) => reconcileWithStore(prev, storeSourcesMap));
    }, [storeSourcesMap]);

    const camerasTotal = camerasData?.page.totalItems || 0;
    const videosTotal = includeVideos ? videosData?.page.totalItems || 0 : 0;

    const myCamerasTotal = myCamerasCountData?.page.totalItems ?? 0;
    const myVideosTotal = includeVideos
        ? (myVideosCountData?.page.totalItems ?? 0)
        : 0;
    const sharedCamerasTotal = myCamerasCountData
        ? Math.max(0, camerasTotal - myCamerasTotal)
        : 0;
    const sharedVideosTotal =
        includeVideos && myVideosCountData
            ? Math.max(0, videosTotal - myVideosTotal)
            : 0;

    const camerasHasNextPage = camerasData
        ? camerasData.page.page < camerasData.page.totalPages
        : false;
    const videosHasNextPage = videosData
        ? videosData.page.page < videosData.page.totalPages
        : false;

    const myFolders = useMemo<SourcesFolderUIItem[]>(
        () =>
            (loadedFolders ?? [])
                .filter((folder: SourceFolder) => folder.isOwner)
                .map(toFolderItem),
        [loadedFolders],
    );

    const sharedFolders = useMemo<SourcesFolderUIItem[]>(
        () =>
            (loadedFolders ?? [])
                .filter((folder: SourceFolder) => !folder.isOwner)
                .map(toFolderItem),
        [loadedFolders],
    );

    const camerasLoadingFirstPage = camerasPage === 1 && camerasLoading;
    const videosLoadingFirstPage = videosPage === 1 && videosLoading;
    const isLoadingSourceTotals =
        camerasLoadingFirstPage || videosLoadingFirstPage;
    const hasSourceTotalsError = Boolean(camerasError || videosError);

    const folders = useMemo<SourceFolderGroup[]>(() => {
        const buildTypeItems = (
            camerasId: string,
            videosId: string,
            camerasCount: number,
            videosCount: number,
        ): SourcesFolderUIItem[] => {
            const camerasItem: SourcesFolderUIItem | null = camerasEnabled
                ? {
                      id: camerasId,
                      name: t('folders.cameras'),
                      count: camerasCount,
                      isLoading: camerasLoadingFirstPage,
                      isDynamic: false,
                  }
                : null;
            const videosItem: SourcesFolderUIItem | null = videosEnabled
                ? {
                      id: videosId,
                      name: t('folders.videos'),
                      count: videosCount,
                      isLoading: videosLoadingFirstPage,
                      isDynamic: false,
                  }
                : null;
            return orderByCount(camerasItem, videosItem);
        };

        const camerasCountOf = (count: number) => (camerasEnabled ? count : 0);
        const videosCountOf = (count: number) => (videosEnabled ? count : 0);

        const groups: SourceFolderGroup[] = [
            {
                id: 'mine',
                title: t('folders.mySources'),
                count:
                    camerasCountOf(myCamerasTotal) +
                    videosCountOf(myVideosTotal),
                defaultExpanded: true,
                items: [
                    ...buildTypeItems(
                        SourceFolderId.MY_CAMERAS,
                        SourceFolderId.MY_VIDEOS,
                        myCamerasTotal,
                        myVideosTotal,
                    ),
                    ...myFolders,
                ],
            },
        ];

        const sharedSourcesCount =
            camerasCountOf(sharedCamerasTotal) +
            videosCountOf(sharedVideosTotal);
        if (sharedSourcesCount + sharedFolders.length > 0) {
            groups.push({
                id: 'shared',
                title: t('folders.sharedWithMe'),
                count: sharedSourcesCount,
                defaultExpanded: false,
                items: [
                    ...buildTypeItems(
                        SourceFolderId.SHARED_CAMERAS,
                        SourceFolderId.SHARED_VIDEOS,
                        sharedCamerasTotal,
                        sharedVideosTotal,
                    ),
                    ...sharedFolders,
                ],
            });
        }
        return groups;
    }, [
        camerasEnabled,
        videosEnabled,
        camerasTotal,
        videosTotal,
        camerasLoadingFirstPage,
        videosLoadingFirstPage,
        myCamerasTotal,
        myVideosTotal,
        sharedCamerasTotal,
        sharedVideosTotal,
        myFolders,
        sharedFolders,
        t,
    ]);

    const sidebarFolders = useMemo<SourcesFolderUIItem[]>(
        () => folders.flatMap((group) => group.items),
        [folders],
    );

    useEffect(() => {
        const fallbackFolderId = resolveEnabledBuiltInFolderId(
            activeFolderId,
            camerasEnabled,
            videosEnabled,
        );
        if (fallbackFolderId) {
            setActiveFolderId(fallbackFolderId);
        }
    }, [activeFolderId, camerasEnabled, videosEnabled]);

    useEffect(() => {
        if (isBuiltInSidebarItem) return;
        if (activeFolderChildrenLoading) return;
        if (activeFolderChildrenError) return;
        if (!activeFolderChildren) return;
        setFolderSourcesByFolderId((previous) => ({
            ...previous,
            [activeFolderId]: activeFolderChildren.sources,
        }));
    }, [
        activeFolderId,
        activeFolderChildren,
        activeFolderChildrenLoading,
        activeFolderChildrenError,
        isBuiltInSidebarItem,
    ]);

    const cameraScopeSources = useMemo<Record<string, FullSource[]>>(
        () => ({
            [SourceFolderId.ALL_CAMERAS]: allCameras,
            [SourceFolderId.MY_CAMERAS]: myCameras,
            [SourceFolderId.SHARED_CAMERAS]: sharedCameras,
        }),
        [allCameras, myCameras, sharedCameras],
    );
    const videoScopeSources = useMemo<Record<string, FullSource[]>>(
        () => ({
            [SourceFolderId.ALL_VIDEOS]: allVideos,
            [SourceFolderId.MY_VIDEOS]: myVideos,
            [SourceFolderId.SHARED_VIDEOS]: sharedVideos,
        }),
        [allVideos, myVideos, sharedVideos],
    );
    const builtInTypeSources = useMemo<Record<string, FullSource[]>>(
        () => ({ ...cameraScopeSources, ...videoScopeSources }),
        [cameraScopeSources, videoScopeSources],
    );

    const cameraScopeHasMore = useMemo<Record<string, boolean>>(
        () => ({
            [SourceFolderId.ALL_CAMERAS]: camerasHasNextPage,
            [SourceFolderId.MY_CAMERAS]:
                camerasHasNextPage && myCameras.length < myCamerasTotal,
            [SourceFolderId.SHARED_CAMERAS]:
                camerasHasNextPage && sharedCameras.length < sharedCamerasTotal,
        }),
        [
            camerasHasNextPage,
            myCameras.length,
            myCamerasTotal,
            sharedCameras.length,
            sharedCamerasTotal,
        ],
    );
    const videoScopeHasMore = useMemo<Record<string, boolean>>(
        () => ({
            [SourceFolderId.ALL_VIDEOS]: videosHasNextPage,
            [SourceFolderId.MY_VIDEOS]:
                videosHasNextPage && myVideos.length < myVideosTotal,
            [SourceFolderId.SHARED_VIDEOS]:
                videosHasNextPage && sharedVideos.length < sharedVideosTotal,
        }),
        [
            videosHasNextPage,
            myVideos.length,
            myVideosTotal,
            sharedVideos.length,
            sharedVideosTotal,
        ],
    );

    const activeFolderView = useMemo(() => {
        const buildScopeView = (
            sources: FullSource[],
            page: number,
            loading: boolean,
            validating: boolean,
            hasMore: boolean,
            error: unknown,
            setPage: React.Dispatch<React.SetStateAction<number>>,
        ) => ({
            sources,
            isLoading: page === 1 && loading,
            isLoadingMore: page > 1 && validating,
            hasMore,
            loadMore: () => {
                if (!validating && hasMore) {
                    setPage((p) => p + 1);
                }
            },
            error,
        });

        if (useBackendScope) {
            return {
                sources: scopeSearchResults,
                isLoading: scopeSearchPage === 1 && scopeSearchLoading,
                isLoadingMore: scopeSearchPage > 1 && scopeSearchLoading,
                hasMore: scopeSearchHasNextPage,
                loadMore: () => {
                    if (!scopeSearchLoading && scopeSearchHasNextPage) {
                        setScopeSearchPage((p) => p + 1);
                    }
                },
                error: scopeSearchError,
            };
        }

        const cameraSources = cameraScopeSources[activeFolderId];
        if (cameraSources) {
            return buildScopeView(
                cameraSources,
                camerasPage,
                camerasLoading,
                camerasValidating,
                cameraScopeHasMore[activeFolderId],
                camerasError,
                setCamerasPage,
            );
        }

        const videoSources = videoScopeSources[activeFolderId];
        if (videoSources) {
            return buildScopeView(
                videoSources,
                videosPage,
                videosLoading,
                videosValidating,
                videoScopeHasMore[activeFolderId],
                videosError,
                setVideosPage,
            );
        }

        const folderSources = activeFolderChildren?.sources || [];
        return {
            sources: filterSourcesByType(folderSources, activeSourceType),
            isLoading: activeFolderChildrenLoading || sourceFoldersLoading,
            isLoadingMore: false,
            hasMore: false,
            loadMore: () => {},
            error: activeFolderChildrenError,
        };
    }, [
        activeFolderId,
        useBackendScope,
        scopeSearchResults,
        scopeSearchPage,
        scopeSearchLoading,
        scopeSearchHasNextPage,
        scopeSearchError,
        cameraScopeSources,
        camerasPage,
        camerasLoading,
        camerasValidating,
        cameraScopeHasMore,
        camerasError,
        videoScopeSources,
        videosPage,
        videosLoading,
        videosValidating,
        videoScopeHasMore,
        videosError,
        activeFolderChildren,
        activeFolderChildrenLoading,
        activeFolderChildrenError,
        sourceFoldersLoading,
        activeSourceType,
    ]);

    const filteredSources = useMemo(() => {
        if (debouncedSearchQuery && !useBackendScope) {
            return activeFolderView.sources.filter((s) =>
                s.name
                    .toLowerCase()
                    .includes(debouncedSearchQuery.toLowerCase()),
            );
        }
        return activeFolderView.sources;
    }, [activeFolderView.sources, debouncedSearchQuery, useBackendScope]);

    const typedFolderSourcesByFolderId = useMemo<
        Readonly<Record<string, readonly FullSource[]>>
    >(() => {
        if (!activeSourceType) return folderSourcesByFolderId;
        return Object.fromEntries(
            Object.entries(folderSourcesByFolderId).map(
                ([folderId, sources]) => [
                    folderId,
                    filterSourcesByType(sources, activeSourceType),
                ],
            ),
        );
    }, [folderSourcesByFolderId, activeSourceType]);

    useEffect(() => {
        setHasUserSelectedFolder(false);
        setHasResolvedDefaultFolder(false);
    }, [activeSourceType]);

    useEffect(() => {
        if (hasUserSelectedFolder || hasResolvedDefaultFolder) return;
        const populatedScope = sidebarFolders.find(
            (folder) => !folder.isDynamic && folder.count > 0,
        );
        if (!populatedScope) return;
        setHasResolvedDefaultFolder(true);
        setActiveFolderId(populatedScope.id);
    }, [hasUserSelectedFolder, hasResolvedDefaultFolder, sidebarFolders]);

    const isLoadingMoreFolders =
        sourceFoldersValidating && sourceFoldersSize > 1;

    return {
        searchQuery,
        setSearchQuery,
        activeFolderId,
        setActiveFolderId: selectFolder,
        sidebarFolders,
        folders,
        activeFolderView,
        filteredSources,
        activeScopeSearchSourceIds,
        allCameras,
        allVideos,
        builtInTypeSources,
        hasMoreFolders,
        loadMoreFolders,
        isLoadingMoreFolders,
        folderSourcesByFolderId: typedFolderSourcesByFolderId,
        scopeSourceIdsByScopeId,
        camerasTotal,
        videosTotal,
        isLoadingSourceTotals,
        hasSourceTotalsError,
    };
}
